import {
  Text,
  HStack,
  Link,
  VStack,
  IconButton,
  ModalHeader,
  ModalBody,
} from '@chakra-ui/react';
import ChevronLeftSharpIcon from '@gamma/ui/icons/ChevronLeftSharpIcon';
import CloseSharpIcon from '@gamma/ui/icons/CloseSharpIcon';
import { i18n } from '@investigator/shared/localization';
import { useEntitiesContext } from '../../../context';
import { useEntityAgenticTriageReportContext } from '../../../context';
import { AiVerdictBadge } from '../../AiVerdictBadge';
import React, { useState, useMemo } from 'react';
import { ModalView } from '@investigator/features/entities/types';
import type {
  KeyFindingsInfo,
  KeyFinding,
} from '@investigator/shared/generated/graphql';
import { KeyFindingsRow } from './KeyFindingsRow';
import { mapVerdictToAIInsightVerdict } from '../../../utils';
import { useParseBackticks } from '@investigator/shared/hooks/useParseBackticks/useParseBackticks';

const modalCopy = i18n.pages.entities.entityAnalysisModal.keyFindings;
const parentModalCopy = i18n.pages.entities.entityAnalysisModal;

export const KeyFindingsHeader: React.FC = () => {
  const {
    analysisModal: { modalNavigation, onClose },
  } = useEntitiesContext();

  const backButtonAction =
    modalNavigation.currentView === ModalView.KeyFindings
      ? modalNavigation.goBackToMain
      : () => modalNavigation.navigateTo(ModalView.KeyFindings);

  return (
    <ModalHeader pt={5} borderBottom="1px solid" borderColor="border.layer.1">
      <HStack justifyContent="space-between" alignItems="center" width="100%">
        <HStack spacing={2}>
          <IconButton
            variant="ghost"
            colorScheme="gray"
            size="sm"
            icon={<ChevronLeftSharpIcon />}
            aria-label="Back"
            onClick={backButtonAction}
          />
          <Text textStyle="h4">{modalCopy.header}</Text>
        </HStack>
        <IconButton
          variant="ghost"
          colorScheme="gray"
          size="sm"
          icon={<CloseSharpIcon />}
          aria-label="Close"
          onClick={onClose}
        />
      </HStack>
    </ModalHeader>
  );
};

export const KeyFindingsView: React.FC = () => {
  const { report } = useEntityAgenticTriageReportContext();
  const { parseBackticks } = useParseBackticks();
  const [isExpanded, setIsExpanded] = useState(false);

  // Map API data to component format
  const keyFindingsData = useMemo((): KeyFindingsInfo => {
    if (!report?.key_findings_info) {
      // Return empty structure if no data
      return {
        summary: null,
        key_findings: null,
      };
    }

    return {
      summary:
        report.key_findings_info.summary ||
        report.analysis_summary?.description ||
        null,
      key_findings:
        report.key_findings_info.key_findings
          ?.filter((f): f is KeyFinding => f !== null)
          ?.map((finding) => ({
            ...finding,
            description: finding.description || '',
          })) || null,
    };
  }, [report]);

  const aiDescription = keyFindingsData.summary || '';

  // Get verdict from report data
  const verdict = report?.analysis_summary?.verdict
    ? mapVerdictToAIInsightVerdict(report.analysis_summary.verdict)
    : undefined;

  const maxChars = 300;
  const isTruncated = aiDescription.length > maxChars;
  const moreLessText = isExpanded ? parentModalCopy.less : parentModalCopy.more;
  const aiDescriptionDisplay = isExpanded
    ? aiDescription
    : aiDescription.slice(0, maxChars) + (isTruncated ? '...' : '');

  return (
    <>
      <KeyFindingsHeader />
      <ModalBody overflowY="auto" flex="1" p={6}>
        <VStack width="100%" spacing={3} align="stretch">
          <VStack mb={3} spacing={2} align="start">
            {verdict && <AiVerdictBadge verdict={verdict} />}
            <Text textStyle="body-md" mt={2}>
              {parseBackticks(aiDescriptionDisplay)}
              {isTruncated && (
                <Link
                  color="blue.400"
                  ml={1}
                  textStyle="body-md"
                  onClick={() => setIsExpanded(!isExpanded)}
                  cursor="pointer"
                >
                  {moreLessText}
                </Link>
              )}
            </Text>
          </VStack>
          {keyFindingsData.key_findings?.map((finding, index) => (
            <KeyFindingsRow
              key={index}
              {...finding}
              descriptionTag={parseBackticks(finding?.description || '')}
              index={index}
            />
          ))}
        </VStack>
      </ModalBody>
    </>
  );
};
